import { createFocusTrap } from 'focus-trap';

export default class Navigation {
  constructor(element) {
    this.element = element;
    this.items = this.element.querySelectorAll('[data-nav-item]');
    this.triggers = this.element.querySelectorAll('[data-nav-trigger]');
    this.dropdowns = this.element.querySelectorAll('[data-nav-dropdown]');
    this.activeClass = 'is-active';
    this.focusClass = 'is-focused';
    this.navOpenClass = 'has-nav-open';
    this.activeItemIndex = -1;
    this.html = document.documentElement;
    this.onActivateBind = this.onActivate.bind(this);
    this.onDeactivateBind = this.onDeactivate.bind(this);
    this.onPostDeactivateBind = this.onPostDeactivate.bind(this);
    this.onResizeBind = this.onResize.bind(this);
    this.currentFocusTrap = null;
    this.focusTraps = [];

    for (const trigger of this.triggers) {
      const container = trigger.parentNode;
      const dropdown = container.querySelector('[data-nav-dropdown]');
      this.focusTraps.push({
        $container: container,
        $trigger: trigger,
        $dropdown: dropdown,
        focusTrap: createFocusTrap(container, {
          onActivate: this.onActivateBind,
          onDeactivate: this.onDeactivateBind,
          onPostDeactivate: this.onPostDeactivateBind,
          clickOutsideDeactivates: true,
        }),
      });
    }

    this.itemsData = [];
    this.debounceDelay = 200;
    this.delay = 150;
  }

  init() {
    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => this.onTriggerClick(event));
    });
    this.onResizeEvent = this.debounce(this.onResizeBind, this.debounceDelay);
    window.addEventListener('resize', this.onResizeEvent);
    this.activeItemIndex = Array.from(this.items).findIndex((item) =>
      item.classList.contains(this.activeClass)
    );
    if (this.activeItemIndex > -1) {
      this.element.classList.add(this.activeClass);
    }
    return this;
  }

  onTriggerClick(event) {
    const item = event.currentTarget.parentNode;
    if (item) this.toggle(item);
  }

  onActivate() {
    this.html.classList.add(this.navOpenClass);
    this.currentFocusTrap.$container.classList.add(this.focusClass);
    this.currentFocusTrap.$trigger.setAttribute('aria-expanded', true);
    this.currentFocusTrap.$dropdown.setAttribute('aria-hidden', false);
  }

  onDeactivate() {
    this.currentFocusTrap.$container.classList.remove(this.focusClass);
    this.currentFocusTrap.$trigger.setAttribute('aria-expanded', false);
    this.currentFocusTrap.$dropdown.setAttribute('aria-hidden', true);
    this.currentFocusTrap = null;
  }

  onPostDeactivate() {
    setTimeout(() => {
      if (this.currentFocusTrap === null) {
        this.html.classList.remove(this.navOpenClass);
      }
    }, this.delay);
  }

  onResize() {
    this.computeDropdownHeight();
  }

  toggle(item) {
    const trapData = this.focusTraps.find((ft) => ft.$container === item);
    if (!trapData) return;
    if (this.currentFocusTrap === trapData) {
      this.close();
    } else {
      this.open(item);
    }
  }

  open(item) {
    const trapData = this.focusTraps.find((ft) => ft.$container === item);
    if (!trapData) return;
    this.close();
    requestAnimationFrame(() => {
      this.currentFocusTrap = trapData;
      this.computeDropdownHeight();
      requestAnimationFrame(() => {
        trapData?.focusTrap?.activate?.();
      });
    });
  }

  close() {
    this.currentFocusTrap?.focusTrap?.deactivate?.();
  }

  computeDropdownHeight() {
    if (this.currentFocusTrap === null) return;
    const height = this.currentFocusTrap.$dropdown.clientHeight;
    this.element.style.setProperty('--dropdown-height', `${height}px`);
  }

  debounce(func, delay) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), delay);
    };
  }
}
